import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as geojson from "geojson";
import ToolbarButtonPopover from "../toolbar/ToolbarButtonPopover";
import { Box, CircularProgress } from "@mui/material";
import { useCustomLayers, useCustomLayerDetail } from "../../api/customLayer";
import { Filter } from "../layerControl/FilterModal";
import { applyFilters } from "../../utils/layerFilters";
import {
  DEFAULT_CUSTOM_LAYER_COLOR,
  DEFAULT_CUSTOM_LAYER_OPACITY,
} from "../../types/customLayer";

/** Darken a #rrggbb hex colour by `amount` (0..1) for the fill outline. */
const darken = (hex: string, amount = 0.18): string => {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.max(0, Math.round(((n >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((n >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((n & 0xff) * (1 - amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

/**
 * CustomLayersLayer - manages selection and display of user-defined vector layers
 */

/**
 * Aggregates toggled-on custom layers into a single GeoJSON source so the map can
 * render them like any other layer. Very similar contract to AssetsLayer.
 */
const CustomLayersLayer = (id: string, name: string, filters: Filter[] = []) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { customLayers } = useCustomLayers(projectId);

  /* Which custom layers are turned on? */
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  /* Cache layerId -> FeatureCollection so we only fetch once */
  const [cache, setCache] = useState<Record<number, geojson.FeatureCollection>>({});

  const [source, setSource] = useState<geojson.FeatureCollection | null>(null);
  const [rawSource, setRawSource] = useState<geojson.FeatureCollection | null>(null);
  const [active, setActive] = useState(false);
  const [stale, setStale] = useState(false);

  /* Process layers that need fetching using the hook */
  const [currentFetchingId, setCurrentFetchingId] = useState<number | null>(null);

  // Setup fetch queue from selectedIds that aren't in cache yet
  useEffect(() => {
    if (!projectId) return;
    const toFetch = selectedIds.filter(id => !cache[id]);
    if (toFetch.length > 0 && currentFetchingId === null) {
      setCurrentFetchingId(toFetch[0]);
    }
  }, [selectedIds, projectId]);

  // Fetch the current layer
  const { layerDetail } = useCustomLayerDetail(projectId, currentFetchingId);

  // Process the fetched layer
  useEffect(() => {
    if (layerDetail && currentFetchingId !== null) {
      // Add to cache
      const geometries = (layerDetail as any)?.data?.geometries || (layerDetail as any)?.geometries;
      if (geometries) {
        setCache(prev => ({ ...prev, [currentFetchingId]: geometries }));
      }
      // Mark as processed and move to next if any
      setCurrentFetchingId(null);
    }
  }, [layerDetail, currentFetchingId]);

  /* Rebuild combined source when cache or selection changes */
  useEffect(() => {
    const features: geojson.Feature[] = [];
    selectedIds.forEach((lid) => {
      const fc = cache[lid];
      if (!fc) return;

      const meta = customLayers?.find((c) => c.id === lid);
      const fillColor = meta?.color ?? DEFAULT_CUSTOM_LAYER_COLOR;
      const fillOpacity = meta?.opacity ?? DEFAULT_CUSTOM_LAYER_OPACITY;
      const outlineColor = darken(fillColor);

      // fc might be an array of Features or a FeatureCollection
      const fcFeatures: geojson.Feature[] = Array.isArray(fc)
        ? (fc as unknown as geojson.Feature[])
        : (fc as any).features || [];

      fcFeatures.forEach((f) => {
        features.push({
          ...f,
          properties: {
            ...f.properties,
            hoverLabel: f.properties?.name || meta?.name,
            _customLayerFillColor: fillColor,
            _customLayerOutlineColor: outlineColor,
            _customLayerOpacity: fillOpacity,
          },
        });
      });
    });
    const baseSource: geojson.FeatureCollection = { type: "FeatureCollection", features };
    setRawSource(baseSource);
    const filtered = applyFilters(baseSource, filters);
    setSource(filtered);
    setActive(selectedIds.length > 0);
    setStale(true);
  }, [cache, selectedIds, customLayers, filters]);

  const toggleLayer = (layerId: number) => {
    setSelectedIds((prev) =>
      prev.includes(layerId) ? prev.filter((id) => id !== layerId) : [...prev, layerId]
    );
  };

  const toolbarButton = (
    <ToolbarButtonPopover
      id={id}
      name={"Custom Layers"}
      enabled={(customLayers?.length || 0) > 0}
      icon="layers"
      toggledOn={active}
      stayOpenAfterSelection
      options={(customLayers || []).map((cl) => {
        const isSelected = selectedIds.includes(cl.id);
        const isLoading = isSelected && !cache[cl.id];
        return {
          id: cl.id.toString(),
          label: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '2px',
                  backgroundColor: cl.color ?? DEFAULT_CUSTOM_LAYER_COLOR,
                  opacity: cl.opacity ?? DEFAULT_CUSTOM_LAYER_OPACITY,
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  flexShrink: 0,
                }}
              />
              <span>{cl.name}</span>
              {isLoading && <CircularProgress size={14} thickness={5} />}
            </Box>
          ),
          onClick: () => toggleLayer(cl.id),
          selected: isSelected,
        };
      })}
    />
  );

  const layerStyle = {
    id: id,
    type: "fill" as const,
    paint: {
      "fill-color": [
        "coalesce",
        ["get", "_customLayerFillColor"],
        DEFAULT_CUSTOM_LAYER_COLOR,
      ],
      "fill-opacity": [
        "coalesce",
        ["get", "_customLayerOpacity"],
        DEFAULT_CUSTOM_LAYER_OPACITY,
      ],
      "fill-outline-color": [
        "coalesce",
        ["get", "_customLayerOutlineColor"],
        darken(DEFAULT_CUSTOM_LAYER_COLOR),
      ],
    },
  };

  return {
    id,
    name,
    source,
    rawSource,
    layerStyle,
    toolbarButton,
    active,
    stale,
    setStale,
    selectedIds,
    toggleLayer,
  };
};

export default CustomLayersLayer;
